import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Alert, Button, Form, Spinner } from "react-bootstrap"
import { Bar } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface VisitDailyTrend {
  date: string
  count: number
  withImage: number
  withCoordinates: number
}

interface VisitTopUser {
  userId: string
  userName: string
  visitCount: number
  uniquePlaces: number
  withImage: number
  withCoordinates: number
}

interface VisitTopOrganization {
  organizationId: string
  organizationName: string
  visitCount: number
  uniqueUsers: number
  uniquePlaces: number
}

interface VisitReviewData {
  period: { days: number; since: string; until: string }
  totalPlaces: number
  totalVisits: number
  uniquePlacesVisited: number
  coveragePercent: number
  visitsWithImage: number
  visitsWithoutImage: number
  visitsWithCoordinates: number
  visitsWithoutCoordinates: number
  visitsWithoutPlace: number
  topUsers: VisitTopUser[]
  topOrganizations: VisitTopOrganization[]
  dailyTrend: VisitDailyTrend[]
}

interface OrganizationOption {
  id: string
  name: string
}

interface VisitAuditCandidate {
  visitDate: string
  vehicleId: number
  vehicleName: string
  siteId: string
  siteName: string
  distance: number
  durationSeconds: number
  positionCount: number
  sourceType: string
}

interface VisitAuditPayload {
  organizationId: string
  organizationName: string
  audit: {
    hoursBack: number
    processedPlaces: number
    positionRecordsReviewed: number
    stopRecordsReviewed: number
    missingPositionCandidates: number
    missingStopCandidates: number
    totalMissingCandidates: number
    createdVisits: number
    sampleCandidates: VisitAuditCandidate[]
  }
}

interface VisitReviewComponentProps {
  organizationId?: string
}

const VisitReviewComponent = ({ organizationId = "" }: VisitReviewComponentProps) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const canChooseOrganization = isSuperAdmin(user) && !organizationId
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const [days, setDays] = useState(30)
  const [hoursBack, setHoursBack] = useState(168)
  const [data, setData] = useState<VisitReviewData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [isAuditing, setIsAuditing] = useState(false)
  const [auditResult, setAuditResult] = useState<VisitAuditPayload | null>(null)
  const [auditError, setAuditError] = useState("")
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState(organizationId || userOrgId)

  const loadOrganizations = useCallback(async () => {
    if (!canChooseOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status !== "ok") return

      const options = (res.data.response || []).map((item: any) => ({
        id: item.id,
        name: item.name,
      }))

      setOrganizations(options)
      if (!selectedOrgId && options.length > 0) {
        setSelectedOrgId(options[0].id)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canChooseOrganization, selectedOrgId])

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      const params = new URLSearchParams()
      params.set("days", String(days))
      if (selectedOrgId) {
        params.set("OrganizationId", selectedOrgId)
      }
      const res = await API.getAction("Platform/VisitReview", params.toString())
      if (res.data.status === "ok") {
        setData(res.data.response)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [days, selectedOrgId])

  const runAudit = useCallback(async (saveMissingVisits: boolean) => {
    if (!selectedOrgId) {
      setAuditError(t("Select an organization before running the review"))
      return
    }

    try {
      setIsAuditing(true)
      setAuditError("")
      const res = await API.postAction("Sync/VehicleVisitAudit", {
        organizationId: selectedOrgId,
        hoursBack,
        includePositions: true,
        includeStops: true,
        saveMissingVisits,
      })

      if (res.data.status === "ok") {
        setAuditResult(res.data.response)
        if (saveMissingVisits) {
          await loadData()
        }
        return
      }

      setAuditError(res.data.response || t("Unable to run visit audit"))
    } catch (ex: any) {
      console.error(ex)
      setAuditError(ex?.response?.data?.response || t("Unable to run visit audit"))
    } finally {
      setIsAuditing(false)
    }
  }, [hoursBack, loadData, selectedOrgId, t])

  useEffect(() => {
    if (canChooseOrganization) {
      loadOrganizations()
    }
  }, [canChooseOrganization, loadOrganizations])

  useEffect(() => {
    setSelectedOrgId(organizationId || userOrgId)
  }, [organizationId, userOrgId])

  useEffect(() => {
    loadData()
  }, [loadData])

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  if (!data) return null

  const dailyChart = {
    labels: data.dailyTrend.map(item => item.date.slice(5)),
    datasets: [
      {
        label: t("Visits"),
        data: data.dailyTrend.map(item => item.count),
        backgroundColor: "#0d6efd",
        borderRadius: 4,
      },
      {
        label: t("With Image"),
        data: data.dailyTrend.map(item => item.withImage),
        backgroundColor: "#198754",
        borderRadius: 4,
      },
      {
        label: t("With Coordinates"),
        data: data.dailyTrend.map(item => item.withCoordinates),
        backgroundColor: "#6f42c1",
        borderRadius: 4,
      },
    ],
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h5 className="fw-bold mb-0">
            <i className="fas fa-route me-2 text-primary" />
            {t("Visit Review")}
          </h5>
          <small className="text-muted">
            {data.period.since} → {data.period.until}
          </small>
        </div>
        <div className="btn-group">
          {[7, 30, 60, 90].map(option => (
            <button
              key={option}
              className={`btn btn-sm ${days === option ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setDays(option)}
            >
              {option}d
            </button>
          ))}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-2">
            {canChooseOrganization && (
              <div style={{ minWidth: 260 }}>
                <label className="form-label small text-muted mb-1">{t("Organization")}</label>
                <Form.Select
                  size="sm"
                  value={selectedOrgId}
                  onChange={e => setSelectedOrgId(e.target.value)}
                  disabled={isLoadingOrganizations || isAuditing}
                >
                  <option value="">{t("Select organization for indicators")}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Form.Select>
              </div>
            )}
            <div style={{ width: 140 }}>
              <label className="form-label small text-muted mb-1">{t("Hours back")}</label>
              <Form.Control
                size="sm"
                type="number"
                min={1}
                max={4320}
                value={hoursBack}
                onChange={e => setHoursBack(Number(e.target.value) || 168)}
                disabled={isAuditing}
              />
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              disabled={isAuditing || !selectedOrgId}
              onClick={() => runAudit(false)}
            >
              {isAuditing ? <Spinner animation="border" size="sm" className="me-1" /> : <i className="fas fa-magnifying-glass me-1" />}
              {t("Analyze missing visits")}
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={isAuditing || !selectedOrgId}
              onClick={() => runAudit(true)}
            >
              <i className="fas fa-rotate me-1" />
              {t("Create missing visits")}
            </Button>
          </div>

          {auditError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {auditError}
            </Alert>
          )}

          {auditResult && (
            <div className="mt-3">
              <div className="small text-muted mb-2">
                {auditResult.organizationName} • {t("Hours back")}: {auditResult.audit.hoursBack}
              </div>
              <div className="row g-2 mb-3">
                {[
                  { label: t("Missing candidates"), value: auditResult.audit.totalMissingCandidates },
                  { label: t("From positions"), value: auditResult.audit.missingPositionCandidates },
                  { label: t("From stops"), value: auditResult.audit.missingStopCandidates },
                  { label: t("Created visits"), value: auditResult.audit.createdVisits },
                ].map(item => (
                  <div className="col-md-3 col-6" key={item.label}>
                    <div className="border rounded p-2 bg-light h-100">
                      <div className="small text-muted">{item.label}</div>
                      <div className="fw-bold">{item.value.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("Date")}</th>
                      <th>{t("Vehicle")}</th>
                      <th>{t("Place")}</th>
                      <th className="text-end">{t("Distance (mts)")}</th>
                      <th className="text-end">{t("Source")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditResult.audit.sampleCandidates.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          {t("No missing visits detected")}
                        </td>
                      </tr>
                    ) : auditResult.audit.sampleCandidates.map(item => (
                      <tr key={`${item.sourceType}-${item.vehicleId}-${item.siteId}-${item.visitDate}`}>
                        <td>{new Date(item.visitDate).toLocaleString()}</td>
                        <td>{item.vehicleName}</td>
                        <td>{item.siteName}</td>
                        <td className="text-end">{Math.round(item.distance)}</td>
                        <td className="text-end text-capitalize">{item.sourceType}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="row mb-4">
        {[
          { label: t("Visit Coverage"), value: `${data.coveragePercent}%`, icon: "fa-bullseye", color: "#0d6efd" },
          { label: t("Visits"), value: data.totalVisits.toLocaleString(), icon: "fa-walking", color: "#198754" },
          { label: t("With Image"), value: data.visitsWithImage.toLocaleString(), icon: "fa-camera", color: "#fd7e14" },
          { label: t("Without Coordinates"), value: data.visitsWithoutCoordinates.toLocaleString(), icon: "fa-location-crosshairs", color: "#dc3545" },
        ].map((item, index) => (
          <div className="col-md-3 col-sm-6 mb-3" key={index}>
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48, backgroundColor: item.color + "22", color: item.color }}
                  >
                    <i className={`fas ${item.icon}`} />
                  </div>
                  <div>
                    <div className="fw-bold fs-5">{item.value}</div>
                    <div className="text-muted small">{item.label}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mb-4">
        <div className="col-lg-7 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visit Registration Trend")}</h6>
              {data.dailyTrend.length > 0 ? (
                <Bar
                  data={dailyChart}
                  options={{
                    responsive: true,
                    plugins: { legend: { position: "top" as const } },
                    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                  }}
                />
              ) : (
                <div className="text-muted small">{t("No data available")}</div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Review Summary")}</h6>
              <div className="small d-flex flex-column gap-2">
                <div className="d-flex justify-content-between"><span>{t("Total Places")}</span><strong>{data.totalPlaces.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Visited Places")}</span><strong>{data.uniquePlacesVisited.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("With Coordinates")}</span><strong>{data.visitsWithCoordinates.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Without Image")}</span><strong>{data.visitsWithoutImage.toLocaleString()}</strong></div>
                <div className="d-flex justify-content-between"><span>{t("Visits Without Place")}</span><strong>{data.visitsWithoutPlace.toLocaleString()}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 mb-3 mb-lg-0">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visits by User")}</h6>
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>{t("User")}</th>
                    <th className="text-end">{t("Visits")}</th>
                    <th className="text-end">{t("Places")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topUsers.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                  ) : data.topUsers.map((item, index) => (
                    <tr key={item.userId}>
                      <td>{index + 1}</td>
                      <td>{item.userName}</td>
                      <td className="text-end">{item.visitCount.toLocaleString()}</td>
                      <td className="text-end">{item.uniquePlaces.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h6 className="fw-semibold mb-3">{t("Visits by Organization")}</h6>
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>{t("Organization")}</th>
                    <th className="text-end">{t("Visits")}</th>
                    <th className="text-end">{t("Users")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topOrganizations.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                  ) : data.topOrganizations.map((item, index) => (
                    <tr key={item.organizationId}>
                      <td>{index + 1}</td>
                      <td>{item.organizationName}</td>
                      <td className="text-end">{item.visitCount.toLocaleString()}</td>
                      <td className="text-end">{item.uniqueUsers.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VisitReviewComponent
